import { useApiClient } from 'src/hooks/useApiClient';
// @ts-ignore
import './StellantisQuoteRequestsPage.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'src/hooks/useAuth';
import toaster from 'src/utils/toaster';
import i18next from 'i18next';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import TableContainer from 'src/components/genericTable/TableContainer';
import { columnsData } from './columnsData';
import { StellantisQuoteRequestDTO } from 'src/api/models/StellantisDTO';
import { QuoteRequestExpand, ReloadAction, StellantisEditQuoteRequestModal, StellantisUtils, UnassignQuoteRequestModal, MakeRequestUnquotableModal, FloatingToolbar } from 'src/components/Stellantis';
import { useErrorToast } from 'src/hooks/useErrorToast';
import ToolTip from 'src/components/Tooltip/Tooltip';
import { HiOutlineUserAdd } from "react-icons/hi";
import { FaRegFilePdf } from 'react-icons/fa';
import { RiArrowGoBackFill } from "react-icons/ri";
import _ from 'lodash';
import { MdBlockFlipped } from 'react-icons/md';
import usePolling from 'src/hooks/usePolling';

const StellantisQuoteRequestsPage = () => {
  const { apiClient, handleApi, handleApiResponse } = useApiClient();
  const [response, setResponse] = useState<StellantisQuoteRequestDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showUnquotableModal, setShowUnquotableModal] = useState(false);
  const [showEditQuoteRequestModal, setShowEditQuoteRequestModal] = useState(false);
  const [loadingState, setLoadingState] = useState({ unassign: false, takeCharge: false, uploadPdf: false, unquotable: false });
  const [selectedQuoteRequest, setSelectedQuoteRequest] = useState<StellantisQuoteRequestDTO | null>(null);
  const { showError } = useErrorToast();
  const skipPageResetRef = useRef<boolean | undefined>();
  const [isPollFetching, setIsPollFetching] = useState(false);

  const getQuoteRequests = async (polling: boolean = false) => {
    polling ? setIsPollFetching(true) : setLoading(true);

    return handleApi(apiClient.stellantisControllerService.getQuoteRequests({ skipLoadingIndicator: polling }))
      .then((res) => {
        setResponse(StellantisUtils.mapQuoteRequests(res));
      })
      .catch((err) => {
        setError(err);
        toaster.error(err.message);
      }).finally(() => {
        setLoading(false);
        polling && setIsPollFetching(false)
      });
  }

  useEffect(() => { getQuoteRequests() }, []);

  const data = useMemo(() => response ?? [], [response]);

  const renderRowSubComponent = useCallback(
    ({ row }) => {
      return <QuoteRequestExpand dto={row.original} />
    },
    []
  )

  const takeCharge = async (quoteRequest: StellantisQuoteRequestDTO | null) => {
    try {
      if (!quoteRequest) return;
      setLoadingState(prev => ({ ...prev, takeCharge: true }));
      await handleApiResponse(apiClient.stellantisControllerService.takeQuoteRequestInCharge(quoteRequest.id));
      toaster.success(i18next.t('quote_update_success'));
      return getQuoteRequests();
    } catch (error) {
      showError(error, i18next.t('quote_update_failed'))
    } finally {
      setLoadingState(prev => ({ ...prev, takeCharge: false }));
      setSelectedQuoteRequest(null);
    }
  }

  const renderActions = useCallback(({ row }) => {
    const quoteRequest = row?.original;
    if (!quoteRequest) return <></>;

    const isPending = StellantisUtils.isPending(quoteRequest);
    const isInProgress = StellantisUtils.isInProgress(quoteRequest);
    const isAssignedToMe = quoteRequest.assignedTo === user.username;

    const buttons: any = [];

    if (isPending) {
      buttons.push(
        (<Button
          id={`take_charge`}
          variant="link"
          key={`take_charge_${row.id}`}
          onClick={() => takeCharge(quoteRequest)}
          className='p-0'
          disabled={loadingState.takeCharge}>
          {loadingState.takeCharge ? <Spinner animation="border" size="sm" /> : <HiOutlineUserAdd size={24} />}
          <ToolTip
            anchorId={`take_charge`}
            content={i18next.t('take_charge')}
            className="xed"
            place="left"
          />
        </Button>)
      )
    }

    if (isInProgress && isAssignedToMe) {
      buttons.push(
        (<Button
          id={`upload_pdf`}
          key={`upload_pdf_${row.id}`}
          variant="link"
          className="text-white"
          disabled={loadingState.uploadPdf}
          onClick={() => {
            setSelectedQuoteRequest(row.original);
            setShowEditQuoteRequestModal(true);
          }}
        >
          {loadingState.uploadPdf ? <Spinner animation="border" size="sm" /> : <FaRegFilePdf size={24} />}
          <ToolTip
            anchorId={`upload_pdf`}
            content={i18next.t('upload_quote_pdf')}
            className="xed"
            place="left"
          />
        </Button>)
      )
    }

    if (isInProgress) {
      const maxChars = 20;
      const anchorId = `info-assignee-${quoteRequest.id}`;
      const assignee = quoteRequest.assignedToFullname || quoteRequest.assignedTo;
      const computedValue = _.truncate(assignee, { 'length': maxChars });

      if (isAssignedToMe) {
        buttons.push(
          <Button
            id={`make_available_${row.id}`}
            key={`make_available_${row.id}`}
            className="text-white"
            onClick={() => {
              setSelectedQuoteRequest(quoteRequest);
              setShowUnassignModal(true);
            }}
            variant="link"
            disabled={loadingState.unassign || !isAssignedToMe}
          >
            {loadingState.unassign ? <Spinner animation="border" size="sm" /> : <RiArrowGoBackFill size={24} />}
            <ToolTip
              anchorId={`make_available_${row.id}`}
              content={i18next.t('make_available')}
              className="xed"
              place="left"
            />
          </Button>,
          <Button
            id={`make_unquotable_${row.id}`}
            key={`make_unquotable_${row.id}`}
            className="text-white"
            onClick={() => {
              setSelectedQuoteRequest(quoteRequest);
              setShowUnquotableModal(true);
            }}
            variant="link"
            disabled={loadingState.unquotable || !isAssignedToMe}
          >
            {loadingState.unquotable ? <Spinner animation="border" size="sm" /> : <MdBlockFlipped size={24} />}
            <ToolTip
              anchorId={`make_unquotable_${row.id}`}
              content={i18next.t('make_unquotable_tooltip_info')}
              className="xed"
              place="left"
            />
          </Button>
        )
      } else {
        buttons.push(<div id={anchorId} key={anchorId}>
          {computedValue}
          <ToolTip
            anchorId={anchorId}
            content={assignee}
            className="xed"
            place="left"
          />
        </div>)
      }
    }

    return buttons.length > 1
      ? <FloatingToolbar items={buttons} />
      : (
        <div className="d-flex align-items-center justify-content-start" key={`actions_${row.id}`}>
          {buttons}
        </div>
      )
  }, [])

  const columns = useMemo(() => {
    return columnsData.map((column) => {
      if (column.id === 'actions') {
        column.Cell = renderActions;
      }
      return column;
    })
  }, []);

  const unassignQuoteRequest = async (quoteRequest: StellantisQuoteRequestDTO | null, reason: string) => {
    try {
      if (!quoteRequest) return;
      setLoadingState(prev => ({ ...prev, unassign: true }));
      await handleApiResponse(apiClient.stellantisControllerService.unassignQuoteRequest(quoteRequest.id, reason));
      return getQuoteRequests();
    } catch (error) {
      showError(error, i18next.t('unassign_quote_request_failed'))
    } finally {
      setLoadingState(prev => ({ ...prev, unassign: false }));
      setSelectedQuoteRequest(null);
    }
  }

  const makeRequestUnquotable = async (quoteRequest: StellantisQuoteRequestDTO | null, reason: string) => {
    try {
      if (!quoteRequest) return;
      setLoadingState(prev => ({ ...prev, unquotable: true }));
      await handleApiResponse(apiClient.stellantisControllerService.makeRequestUnquotable(quoteRequest.id, reason));
      return getQuoteRequests();
    } catch (error) {
      showError(error, i18next.t('unquotable_quote_request_failed'))
    } finally {
      setLoadingState(prev => ({ ...prev, unquotable: false }));
      setSelectedQuoteRequest(null);
    }
  }

  const isEditModalOpen = selectedQuoteRequest && showEditQuoteRequestModal;

  const pollRequestsHandler = () => {
    if (isPollFetching) return;
    getQuoteRequests(true)
  }

  usePolling({ callback: pollRequestsHandler })

  return (
    <Container fluid>
      <Row>
        <Col md={6} className="display-6">
          {i18next.t('quotation_requested_page_title')}
        </Col>
        <Col md={6} className="mt-4 mt-md-0 text-md-end">
          <ReloadAction
            actionName="stellantis-requests"
            isLoading={loading}
            action={getQuoteRequests}
            tooltip={i18next.t('reload')}
          />
        </Col>
      </Row>
      <TableContainer
        skipPageResetRef={skipPageResetRef}
        columns={columns}
        data={data}
        loading={loading}
        error={error}
        isExpandable
        // rowProps={getRowProps}
        renderRowSubComponent={renderRowSubComponent}
      ></TableContainer>

      <UnassignQuoteRequestModal
        isOpen={showUnassignModal}
        doUnassign={(reason: string) => {
          setShowUnassignModal(false);
          unassignQuoteRequest(selectedQuoteRequest, reason)
        }}
        closeModal={() => setShowUnassignModal(false)}
      />

      <MakeRequestUnquotableModal
        isOpen={showUnquotableModal}
        doUnquotable={(reason: string) => {
          setShowUnquotableModal(false);
          makeRequestUnquotable(selectedQuoteRequest, reason)
        }}
        closeModal={() => setShowUnquotableModal(false)}
      />

      {isEditModalOpen &&
        <StellantisEditQuoteRequestModal
          isOpen={showEditQuoteRequestModal}
          quoteRequest={selectedQuoteRequest}
          closeModal={() => {
            setShowEditQuoteRequestModal(false)
            getQuoteRequests();
          }}
        />
      }
    </Container>
  );
};

export default StellantisQuoteRequestsPage;
